import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Statistic, Alert, Empty, message } from "antd";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import analyticsApi from "../../../api/analytics-api";
import { getChartTheme } from "../../../theme/chart-theme";
import { useThemeMode } from "../../../theme/theme-provider";
import ExternalSyncStatusBar from "./external-sync-status-bar";
import ExternalRawTable from "./external-raw-table";

// Tab "Nguồn ngoài" của trang /app-growth — phần đầu phễu nằm NGOÀI DB của mình.
//
// Mục đích: nối liền phễu  impressions → xem trang → lượt tải → máy có token → đơn.
// Bốn bậc đầu ở store, ba bậc cuối trong DB. Nhìn rời rạc thì không ai biết chỗ hẹp
// thật nằm đâu.
//
// ⚠️ BA QUY CHIẾU NGÀY KHÁC NHAU (Apple UTC · Play giờ Thái Bình Dương · DB mình ngày
// VN) nên mỗi nguồn vẽ BIỂU ĐỒ RIÊNG, cố ý không gộp một trục. Gộp trục là mời người
// đọc tưởng cùng một ngày.
// ⚠️ Số của Apple là OPT-IN ONLY ⇒ thấp hơn thực tế. Có chú thích cố định bên dưới.

const DEFAULT_DAYS = 30;

/** Ngày 'YYYY-MM-DD' của N ngày trước (tính bằng UTC cho khỏi dính timezone máy). */
function daysAgo(n) {
    return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
}

const nf = new Intl.NumberFormat("vi-VN");

function AppGrowthExternalTab() {
    const [rows, setRows] = useState([]);
    const [syncState, setSyncState] = useState([]);
    const [loading, setLoading] = useState(false);
    const { isDark } = useThemeMode();
    const theme = getChartTheme(isDark);
    // chart-theme trả tooltipBg/Border/Text rời, KHÔNG có sẵn object style —
    // ghép ở đây đúng khuôn app-growth-charts.js để hai tab nhìn giống nhau.
    const tipStyle = {
        backgroundColor: theme.tooltipBg,
        border: `1px solid ${theme.tooltipBorder}`,
        color: theme.tooltipText,
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await analyticsApi.getAppGrowthExternal({
                from: daysAgo(DEFAULT_DAYS),
                to: daysAgo(0),
            });
            setRows(res.data.data?.rows || []);
            setSyncState(res.data.data?.syncState || []);
        } catch (err) {
            console.error("[AppGrowthExternal] fetch error:", err);
            message.error("Không tải được số liệu nguồn ngoài");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Tách theo nguồn và đảo về XƯA→NAY cho biểu đồ (API trả mới-nhất-trước).
    const bySource = useMemo(() => {
        const out = {};
        for (const r of rows) {
            (out[r.source] = out[r.source] || []).push(r);
        }
        for (const k of Object.keys(out)) out[k].reverse();
        return out;
    }, [rows]);

    const ios = bySource.appstore || [];
    const android = bySource.play || [];

    // Tổng cả kỳ. Cộng bỏ qua ô null (nguồn không có chỉ số đó cho ngày đó).
    const sum = (list, key) => list.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

    const iosDownloads = sum(ios, "downloads_first_time");
    const iosImpressions = sum(ios, "impressions");
    // Conversion TÍNH Ở ĐÂY, không lưu DB — lưu số dẫn xuất rồi sẽ có ngày lệch số gốc.
    const conversion = iosImpressions > 0 ? (iosDownloads / iosImpressions) * 100 : null;

    const hasAnyData = rows.length > 0;

    return (
        <div className="space-y-4">
            <ExternalSyncStatusBar syncState={syncState} loading={loading} />

            <Alert
                type="warning"
                showIcon
                message="Ba mốc ngày khác nhau — đừng đọc ngang hàng"
                description={
                    <ul className="m-0 pl-4 text-xs">
                        <li>
                            <b>App Store</b> chốt ngày theo <b>UTC</b>, <b>Google Play</b> theo{" "}
                            <b>giờ Thái Bình Dương</b>, còn tab “Nội bộ” theo <b>ngày VN</b>. Lệch nhau
                            tới một ngày — vì vậy mỗi nguồn vẽ biểu đồ riêng, cố ý không gộp một trục.
                        </li>
                        <li>
                            Số của Apple là <b>opt-in only</b> (chỉ gồm máy đồng ý chia sẻ) nên{" "}
                            <b>thấp hơn thực tế</b>. Dùng xem xu hướng, không dùng đối soát.
                        </li>
                        <li>
                            <b>Lượt tải</b> (ở đây) ≠ <b>“Máy mới”</b> ở tab Nội bộ. Tab kia đếm máy mới
                            đăng ký <i>push token</i>. Chênh lệch giữa hai số chính là{" "}
                            <b>người cài app nhưng không cấp quyền thông báo</b> — một chỉ số riêng, không
                            phải lỗi dữ liệu.
                        </li>
                        <li>Ngày gần nhất trống là <b>bình thường</b>: store trả số chậm 1–3 ngày.</li>
                    </ul>
                }
            />

            {!loading && !hasAnyData ? (
                <Card size="small">
                    <Empty description="Chưa có dữ liệu — nguồn ngoài chưa được cấu hình credential. Xem thanh trạng thái phía trên." />
                </Card>
            ) : null}

            <Row gutter={[12, 12]}>
                <Col xs={12} md={6}>
                    <Card size="small" loading={loading}>
                        <Statistic title={`Lượt tải lần đầu — iOS (${DEFAULT_DAYS} ngày)`} value={nf.format(iosDownloads)} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={loading}>
                        <Statistic title="Tải lại — iOS" value={nf.format(sum(ios, "redownloads"))} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={loading}>
                        <Statistic
                            title="Tỷ lệ chuyển đổi trang store — iOS"
                            value={conversion === null ? "—" : `${conversion.toFixed(1)}%`}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={loading}>
                        <Statistic
                            title="Lượt cài − lượt gỡ — Android"
                            value={nf.format(sum(android, "downloads_first_time") - sum(android, "uninstalls"))}
                        />
                    </Card>
                </Col>
            </Row>

            <Card size="small" title="iOS — phễu trang App Store (mốc ngày UTC)" loading={loading}>
                {ios.length === 0 ? <Empty description="Chưa có dữ liệu iOS" /> : (
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={ios}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                            <XAxis dataKey="stat_date" stroke={theme.axis} fontSize={11} />
                            <YAxis stroke={theme.axis} fontSize={11} />
                            <Tooltip contentStyle={tipStyle} />
                            <Legend />
                            <Line type="monotone" dataKey="impressions" name="Hiển thị" stroke="#8884d8" dot={false} />
                            <Line type="monotone" dataKey="product_page_views" name="Xem trang" stroke="#82ca9d" dot={false} />
                            <Line type="monotone" dataKey="downloads_first_time" name="Tải lần đầu" stroke="#ff7300" dot={false} />
                            <Line type="monotone" dataKey="redownloads" name="Tải lại" stroke="#aaa" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>

            <Card size="small" title="Android — cài vs gỡ (mốc ngày giờ Thái Bình Dương)" loading={loading}>
                {android.length === 0 ? <Empty description="Chưa có dữ liệu Android" /> : (
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={android}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                            <XAxis dataKey="stat_date" stroke={theme.axis} fontSize={11} />
                            <YAxis stroke={theme.axis} fontSize={11} />
                            <Tooltip contentStyle={tipStyle} />
                            <Legend />
                            <Line type="monotone" dataKey="downloads_first_time" name="Lượt cài" stroke="#ff7300" dot={false} />
                            <Line type="monotone" dataKey="uninstalls" name="Lượt gỡ" stroke="#d4380d" dot={false} />
                            <Line type="monotone" dataKey="active_devices" name="Máy còn giữ app" stroke="#82ca9d" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>

            <ExternalRawTable rows={rows} loading={loading} />
        </div>
    );
}

export default AppGrowthExternalTab;
